import React, { FunctionComponent } from 'react'
import { Heading, Text, Grid, Message, getCountryEmoji } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useConsumerSizes } from '../../../utils/responsive'
import { useConsumerContactAddress, useConsumerEmail, useConsumerMobile, useConsumerPreferredLocale } from '../../../state'
import { Head } from '../../Head'
import { CloseOrReactivateAccountButton } from '../../CloseOrReactivateAccountButton'
import { ConsumerAddress } from '@afterpaytouch/portal-api'
import { mapLocales } from '../../../utils/mapLocales'
import { useEditEmailAddressModal, EditEmailAddressModal } from '../../EditEmailAddressModal'
import { useEditMobileNumberModal, EditMobileNumberModal } from '../../EditMobileNumberModal'
import { useEditAddressModal, EditAddressModal } from '../../EditAddressModal'
import { useEditLanguageModal, EditLanguageModal } from '../../EditLanguageModal'
import { useFormatMobile } from '../../../hooks'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { DetailRow } from '../DetailRow/DetailRow'
import { SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['common', 'account']

export const formatResidentialAddress = (addr: ConsumerAddress): string => {
  if (addr == null) {
    return ''
  }
  const substring = `, ${addr.suburb} ${addr.state} ${addr.postcode}`
  return `${addr.address1.includes(substring) ? addr.address1.split(substring)[0] : addr.address1}${
    addr.address2 != null && addr.address2.length > 0 ? `, ${addr.address2}` : ''
  }, ${addr.suburb != null && addr.suburb.length > 0 ? `${addr.suburb}, ` : ''}${addr.state != null && addr.state.length > 0 ? `${addr.state}, ` : ''}${
    addr.postcode
  }`
}

const FormattedMobileWithEmoji = ({ mobile, country }): JSX.Element => {
  const formattedMobile = useFormatMobile(mobile)
  const countryEmoji = getCountryEmoji(country) ?? ''
  if (formattedMobile !== '' && countryEmoji !== '') {
    return (
      <>
        <span className='mr-2'>{countryEmoji}</span>
        <span>{formattedMobile}</span>
      </>
    )
  } else {
    // @ts-ignore: OPERATION BLEED STOPPER
    return null
  }
}

export const ProfileTab: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const router = useRouter()

  const locale = router?.locale as SupportedLocale
  const country = mapLocales('locale', locale)
  const { subheadingSize } = useConsumerSizes()
  const email = useConsumerEmail()
  const mobile = useConsumerMobile()
  const contactAddress = useConsumerContactAddress()
  const preferredLocale = useConsumerPreferredLocale()

  const isEditLanguageEnabled = (locale === 'en-CA' || locale === 'fr-CA') && (preferredLocale === 'en-CA' || preferredLocale === 'fr-CA')

  const pageTitle = `${t('account:tab:profile:title')} - ${t('account:title')} | ${t('common:brandName')}`

  const [isEditEmailAddressModalOpen, { openModal: openEditEmailAddressModal, closeModal: closeEditEmailAddressModal }] = useEditEmailAddressModal()
  const [isEditMobileNumberModalOpen, { openModal: openEditMobileNumberModal, closeModal: closeEditMobileNumberModal }] = useEditMobileNumberModal()
  const [isEditAddressModalOpen, { openModal: openEditAddressModal, closeModal: closeEditAddressModal }] = useEditAddressModal()
  const [isEditLanguageModalOpen, { openModal: openEditLanguageModal, closeModal: closeEditLanguageModal }] = useEditLanguageModal()

  return (
    <MaskedElement>
      <Head title={pageTitle} />
      <Heading bold size={subheadingSize} testNameSpace='body-title'>
        {t('account:tab:profile:body:title')}
      </Heading>
      <div className='mt-8'>
        <Grid>
          <Grid.Item sm={12} lg={6}>
            <DetailRow
              label={t('account:tab:profile:body:labelEmail')}
              value={email}
              isEditable
              onClick={openEditEmailAddressModal}
              testNameSpace='edit-email'
            />
            <DetailRow
              label={t('account:tab:profile:body:labelMobile')}
              value={<FormattedMobileWithEmoji mobile={mobile} country={country} />}
              isEditable
              onClick={openEditMobileNumberModal}
              testNameSpace='edit-mobile'
            />
            <DetailRow
              label={t('account:tab:profile:body:labelResidentialAddress')}
              // @ts-ignore: OPERATION BLEED STOPPER
              value={formatResidentialAddress(contactAddress)}
              isEditable
              onClick={openEditAddressModal}
              testNameSpace='edit-address'
            />
            {isEditLanguageEnabled && (
              <DetailRow
                label={t('account:tab:profile:body:labelLanguage')}
                value={
                  {
                    'en-CA': t('account:edit:language:content:en-CA'),
                    'fr-CA': t('account:edit:language:content:fr-CA'),
                  }[preferredLocale]
                }
                isEditable
                onClick={openEditLanguageModal}
                testNameSpace='edit-language'
              />
            )}
            <div className='mb-8'>
              <CloseOrReactivateAccountButton />
            </div>
          </Grid.Item>
          <Grid.Item sm={12} lg={6}>
            <div className='lg:flex lg:flex-row lg:justify-center lg:px-8'>
              <div className='max-w-[368px]'>
                <Message
                  kind='Success'
                  iconName='Lock'
                  size='Regular'
                  heading={t('account:tab:profile:body:message:title')}
                  description={
                    <Text size='XS'>
                      <Trans i18nKey={'account:tab:profile:body:message:description'} />
                    </Text>
                  }
                />
              </div>
            </div>
          </Grid.Item>
        </Grid>
      </div>
      <EditEmailAddressModal isModalOpen={isEditEmailAddressModalOpen} closeModal={closeEditEmailAddressModal} />
      <EditMobileNumberModal isModalOpen={isEditMobileNumberModalOpen} closeModal={closeEditMobileNumberModal} />
      <EditAddressModal isModalOpen={isEditAddressModalOpen} closeModal={closeEditAddressModal} country={country} />
      {isEditLanguageEnabled && <EditLanguageModal isModalOpen={isEditLanguageModalOpen} closeModal={closeEditLanguageModal} />}
    </MaskedElement>
  )
}
